import moment from "moment-timezone"

import {
    AvailableModalityCoverageStatus,
    AvailablePlanStatus,
    CalculatorResultType,
    PayerCoverageCheckNetwork,
    ResetBenefitStatus,
} from "@common-shared/config/coverage.config.ts"
import {
    AgenticCheckStatus,
    OneVerifyFlagType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import {
    OneVerifyEhrWriteStatus,
    PoliciesListType,
    PolicyAssignmentPriority,
    PolicyDenialRisk,
    PolicyModality,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import {
    ColumnValueCapitalizationLevel,
    PolicyColumnKey,
} from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config.ts"
import {
    IPolicyColumnConfiguration,
    IPolicyColumnFormatting,
} from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { AgenticCheckStatusComponent } from "../components/policies/agentic/agenticCheckStatus.component"
import { CHECKER_CONFIG } from "../config/checker.config"
import { COVERAGE_CONFIG } from "../config/coverage.config"
import { GENERAL_CONFIG } from "../config/general.config"
import { POLICIES_CONFIG } from "../config/policies.config"
import { POLICIES_COLUMNS_CONFIG } from "../config/policiesColumns.config"
import { UtilHelper } from "./util.helper"

import benefitsNotRequiredIcon from "@front-shared/assets/images/icons/reset-benefits-action-not-required.svg"
import benefitsRequiredIcon from "@front-shared/assets/images/icons/reset-benefits-action-required.svg"
import benefitsNotDetectedIcon from "@front-shared/assets/images/icons/reset-benefits-not-detected.svg"

/**
 * Same class should exist in the backend, just without the JSX support -> CoveragePortalExportReportHelper
 */
export class ExportReportHelper {
    /**
     * Get mapped policy column value based on coverage check
     */
    static getMappedColumnValue = (
        coverageCheckHistory: IOneVerifyCoverageCheckHistory,
        columnConfig: IPolicyColumnConfiguration,
        emptyColumnValue: string | null = null,
        jsxSupportedFormatting = true,
        onlyCheckResult = false,
    ): JSX.Element | string | null => {
        let columnValue: any = columnConfig.isCustomValue
            ? ExportReportHelper.getMappedColumnCustomValue(
                  columnConfig.columnKey,
                  coverageCheckHistory,
              )
            : onlyCheckResult
              ? coverageCheckHistory.coverageResult
              : columnConfig.source
                ? coverageCheckHistory[columnConfig.source]
                : undefined

        emptyColumnValue = this.resolveEmptyColumnValue(
            columnConfig,
            coverageCheckHistory,
            emptyColumnValue,
        )

        if (
            !columnValue &&
            !(
                columnConfig.alternativeSource &&
                columnConfig.alternativeSourcePropertyPath
            ) &&
            !columnConfig.formatOnEmptyColumn
        ) {
            return emptyColumnValue
        }

        // Go through each value path and assign it's value to column value, break down by "." so we can support nested properties of objects
        // Only in case there's no custom value
        if (!columnConfig.isCustomValue && columnConfig.sourcePropertyPath) {
            columnConfig.sourcePropertyPath.split(".").forEach(pathPart => {
                try {
                    columnValue = columnValue?.[pathPart]
                } catch (e) {
                    columnValue = emptyColumnValue
                }
            })
        }

        // IF resulting value is undefined or null -> assign empty string to it
        if (typeof columnValue === "undefined" || columnValue === null) {
            columnValue = emptyColumnValue
        }

        // Try with alternative source
        if (
            columnValue === emptyColumnValue &&
            columnConfig.alternativeSource &&
            columnConfig.alternativeSourcePropertyPath
        ) {
            return ExportReportHelper.getMappedColumnValue(
                coverageCheckHistory,
                {
                    ...columnConfig,
                    source: columnConfig.alternativeSource,
                    sourcePropertyPath:
                        columnConfig.alternativeSourcePropertyPath,
                    alternativeSource: undefined,
                    alternativeSourcePropertyPath: undefined,
                },
                emptyColumnValue,
                jsxSupportedFormatting,
                onlyCheckResult,
            )
        }

        columnValue = ExportReportHelper.getFormattedColumnValue(
            columnValue,
            columnConfig,
            emptyColumnValue,
            jsxSupportedFormatting,
            coverageCheckHistory,
        )

        // If we need to compare with resulted value, find resulted data and compare - add to csv only if differs
        if (columnConfig.dataCompareWith) {
            const compareValue = ExportReportHelper.getMappedColumnValue(
                coverageCheckHistory,
                {
                    ...columnConfig,
                    ...columnConfig.dataCompareWith,

                    dataCompareWith: undefined,
                },
                emptyColumnValue,
                jsxSupportedFormatting,
                onlyCheckResult,
            )

            if (
                !columnValue ||
                (typeof columnValue === "string" &&
                    typeof compareValue === "string" &&
                    columnValue.toLowerCase() === compareValue.toLowerCase())
            ) {
                return emptyColumnValue
            }
        }

        return columnValue
    }

    /**
     * Get custom value for columns that require this
     */
    private static getMappedColumnCustomValue(
        columnKey: PolicyColumnKey,
        data: IOneVerifyCoverageCheckHistory,
    ): string | null {
        let firstName: string | undefined
        let lastName: string | undefined

        switch (columnKey) {
            case PolicyColumnKey.PATIENT_FULL_NAME:
                firstName =
                    data.coverageResult.demographics?.firstName ||
                    data.requestData.firstName

                lastName =
                    data.coverageResult.demographics?.lastName ||
                    data.requestData.lastName

                return lastName && firstName
                    ? `${lastName}, ${firstName}`
                    : COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown

            case PolicyColumnKey.SUBSCRIBER_NAME:
                firstName =
                    data.coverageResult.subscriberDemographics?.firstName

                lastName = data.coverageResult.subscriberDemographics?.lastName

                return lastName && firstName ? `${lastName}, ${firstName}` : ""

            case PolicyColumnKey.REQUESTED_PAYER:
                return data.requestPayer
                    ? `${
                          data.requestPayer.insuranceName
                      } (${data.requestPayer.payerId.toUpperCase()})`
                    : ""

            case PolicyColumnKey.IDENTIFIED_PAYER:
                return data.responsePayer
                    ? `${
                          data.responsePayer.insuranceName
                      } (${data.responsePayer.payerId.toUpperCase()})`
                    : ""

            case PolicyColumnKey.MEDICARE_ADVANTAGE_PAYER_NAME:
                const medicareAdvantage = data.coverageResult?.medicareAdvantage

                if (!medicareAdvantage) {
                    return COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                }

                const payerParts = [
                    medicareAdvantage.payerName,
                    medicareAdvantage.payerId,
                ].filter(
                    item => item !== null && item !== undefined && item !== "",
                )

                return payerParts.length > 0
                    ? payerParts.join(" / ")
                    : COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA

            case PolicyColumnKey.PLAN_BEGIN_END_DATE:
                const { planBeginDate, planEndDate } = data.coverageResult

                if (!planBeginDate && !planEndDate) {
                    return COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                }

                return `${
                    planBeginDate
                        ? moment(planBeginDate).format(
                              GENERAL_CONFIG.defaultMomentDateFormat,
                          )
                        : COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                } - ${
                    planEndDate
                        ? moment(planEndDate).format(
                              GENERAL_CONFIG.defaultMomentDateFormat,
                          )
                        : COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                }`

            case PolicyColumnKey.ELIGIBILITY_BEGIN_END_DATE:
                const { eligibilityBeginDate, eligibilityEndDate } =
                    data.coverageResult

                if (!eligibilityBeginDate && !eligibilityEndDate) {
                    return COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                }

                return `${
                    eligibilityBeginDate
                        ? moment(eligibilityBeginDate).format(
                              GENERAL_CONFIG.defaultMomentDateFormat,
                          )
                        : COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                } - ${
                    eligibilityEndDate
                        ? moment(eligibilityEndDate).format(
                              GENERAL_CONFIG.defaultMomentDateFormat,
                          )
                        : COVERAGE_CONFIG.defaultFallbackCoverageDataValueNA
                }`

            case PolicyColumnKey.PROVIDER_NAME:
                return data.requestData?.customFields?.provider
                    ? data.requestData.customFields.provider
                    : data.requestData?.providerFirstName &&
                        data.requestData?.providerLastName
                      ? `${data.requestData.providerLastName}, ${data.requestData.providerFirstName}`
                      : null

            case PolicyColumnKey.EHR_WRITE_STATUS: {
                const rowRequestId = data.coverageResult?.requestId
                const ehrWriteLog = data.policy?.ehrWriteLog

                if (
                    rowRequestId &&
                    ehrWriteLog?.nirvanaRequestId === rowRequestId
                ) {
                    return ehrWriteLog.status
                }

                return null
            }

            case PolicyColumnKey.NETWORK_STATUS_ALERT: {
                return data.coverageResult?.networkStatusAlert?.isAlert
                    ? POLICIES_CONFIG.networkStatusAlertTriggeredLabel
                    : ""
            }

            default:
                return ""
        }
    }

    /**
     * Get formatted column value based on config
     */
    static getFormattedColumnValue(
        columnValue: any,
        columnConfig: IPolicyColumnConfiguration,
        emptyColumnValue: string | null = null,
        jsxSupportedFormatting = true,
        coverageCheckHistory?: IOneVerifyCoverageCheckHistory,
    ): JSX.Element | string | null {
        if (
            columnConfig.formatting &&
            (columnValue !== emptyColumnValue ||
                columnConfig.formatOnEmptyColumn)
        ) {
            columnValue = ExportReportHelper.applyColumnFormatting(
                columnValue,
                columnConfig.formatting,
                emptyColumnValue,
                jsxSupportedFormatting,
                coverageCheckHistory,
            )
        }

        try {
            if (
                columnValue === emptyColumnValue ||
                columnConfig.ignoreValueCapitalization
            ) {
                return columnValue
            }

            const capitalizationLevel =
                columnConfig.valueCapitalizationLevel ||
                ColumnValueCapitalizationLevel.EACH_WORD

            switch (capitalizationLevel) {
                case ColumnValueCapitalizationLevel.EACH_WORD:
                    return UtilHelper.capitalizeWordsInSentence(columnValue)

                case ColumnValueCapitalizationLevel.FIRST_WORD:
                    return UtilHelper.capitalizeFirstLetter(columnValue)
            }
        } catch (e) {
            return columnValue
        }
    }

    /**
     * Get formatting of some value based on formatting config
     */
    static applyColumnFormatting(
        columnValue: any,
        formatting: IPolicyColumnFormatting,
        emptyColumnValue: any,
        jsxSupportedFormatting = false,
        coverageCheckHistory?: IOneVerifyCoverageCheckHistory,
    ): any {
        try {
            const originalValue = columnValue

            if (columnValue && formatting.enumRenderedMapping) {
                columnValue =
                    formatting.enumRenderedMapping[columnValue]?.displayValue ||
                    columnValue
            }

            if (columnValue && formatting.enumMapping) {
                columnValue = formatting.enumMapping[columnValue] || columnValue
            }

            if (formatting.ehrWriteStatus && jsxSupportedFormatting) {
                const color =
                    POLICIES_CONFIG.oneVerifyEhrWriteStatusColorMapping[
                        originalValue as OneVerifyEhrWriteStatus
                    ] || NIRVANA_COLORS.brand.grey7

                columnValue = <span style={{ color }}>{columnValue}</span>
            }

            if (formatting.currency) {
                let amount = parseFloat(columnValue.toString())

                // Convert to cents if it's not already in cents
                if (!formatting.isCents) {
                    amount = UtilHelper.formatDollarsToCents(amount)
                }

                if (isNaN(amount)) {
                    columnValue = emptyColumnValue
                } else {
                    // Convert to $
                    columnValue = UtilHelper.getFormattedMoney(
                        amount,
                        formatting.showZeroFractionDigits,
                    )
                }
            }

            if (formatting.dateFormat) {
                columnValue = moment(columnValue).format(formatting.dateFormat)
            }

            if (formatting.suffixValue) {
                columnValue = `${columnValue}${formatting.suffixValue}`
            }

            if (formatting.isCoverageStatusValue) {
                columnValue =
                    columnValue === CalculatorResultType.noCoverage
                        ? AvailableModalityCoverageStatus.INACTIVE
                        : CHECKER_CONFIG.activeCoverageResults.includes(
                                columnValue,
                            )
                          ? AvailableModalityCoverageStatus.ACTIVE
                          : AvailableModalityCoverageStatus.UNKNOWN
            }

            if (formatting.resultType) {
                columnValue = columnValue
                    ? CHECKER_CONFIG.activeCoverageResults.includes(columnValue)
                        ? CHECKER_CONFIG.calculatorResultTypeTitleMapping
                              .activeCoverage
                        : CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                              columnValue as CalculatorResultType
                          ] || null
                    : null
            }

            if (formatting.isNetworkTypeValue) {
                columnValue = columnValue
                    ? PayerCoverageCheckNetwork.IN
                    : PayerCoverageCheckNetwork.OUT
            }

            if (formatting.isInNetworkCheckMapped) {
                columnValue = columnValue
                    ? PayerCoverageCheckNetwork.IN
                    : PayerCoverageCheckNetwork.OUT

                columnValue =
                    COVERAGE_CONFIG.payerCoverageCheckNetworkMapped[
                        columnValue as PayerCoverageCheckNetwork
                    ]?.displayValue || columnValue
            }

            if (formatting.address) {
                columnValue =
                    UtilHelper.getFormattedDemographicsAddress(columnValue)
            }

            if (formatting.planStatus) {
                columnValue = CHECKER_CONFIG.activePlanStatuses.includes(
                    columnValue,
                )
                    ? AvailablePlanStatus.active
                    : columnValue
            }

            if (formatting.modalityCoverageStatus) {
                columnValue =
                    COVERAGE_CONFIG.availableModalityCoverageStatusMapping[
                        columnValue as AvailableModalityCoverageStatus
                    ] ||
                    COVERAGE_CONFIG.availableModalityCoverageStatusMapping[
                        AvailableModalityCoverageStatus.UNKNOWN
                    ]
            }

            if (formatting.flagsList) {
                let icon =
                    POLICIES_CONFIG.policyDenialRiskIconsMapping[
                        PolicyDenialRisk.NONE
                    ]

                let text = "No Alerts"

                if (Array.isArray(columnValue) && columnValue.length) {
                    const mappings = (columnValue as OneVerifyFlagType[]).map(
                        item => POLICIES_CONFIG.flagTypeMappings[item],
                    )

                    text = mappings.map(item => item.label).join("\n")

                    const denialRisk = this.getPolicyDenialRisk(
                        columnValue as OneVerifyFlagType[],
                    )

                    icon =
                        POLICIES_CONFIG.policyDenialRiskIconsMapping[denialRisk]
                }

                columnValue = jsxSupportedFormatting ? (
                    <div className="flex items-start">
                        <div className="relative top-[-2px] mr-[8px]">
                            <img src={icon} alt="flag icon" />
                        </div>

                        <PrimaryText>
                            {text.split("\n").map((str, index) => (
                                <div key={index}>{str}</div>
                            ))}
                        </PrimaryText>
                    </div>
                ) : (
                    text.split("\n").join(", ")
                )
            }

            if (formatting.denialRisk) {
                if (!Array.isArray(columnValue) || !columnValue.length) {
                    columnValue =
                        POLICIES_CONFIG.denialRiskMapping[PolicyDenialRisk.NONE]
                } else {
                    const denialRisk = this.getPolicyDenialRisk(
                        columnValue as OneVerifyFlagType[],
                    )

                    columnValue = POLICIES_CONFIG.denialRiskMapping[denialRisk]
                }
            }

            if (typeof columnValue === "boolean" && formatting.qmbStatus) {
                columnValue = columnValue ? "Yes" : "No"
            }

            if (columnValue && formatting.resetBenefitStatus) {
                const mappedValue =
                    COVERAGE_CONFIG.selectRenderedResetBenefitStatus[
                        columnValue as ResetBenefitStatus
                    ]?.displayValue

                if (!jsxSupportedFormatting) {
                    columnValue = mappedValue
                } else {
                    let icon = benefitsNotDetectedIcon

                    switch (columnValue) {
                        case ResetBenefitStatus.DETECTED_NO_ACTION_REQUIRED:
                            icon = benefitsNotRequiredIcon

                            break

                        case ResetBenefitStatus.DETECTED_ACTION_REQUIRED:
                            icon = benefitsRequiredIcon

                            break
                    }

                    columnValue = (
                        <div className="flex items-center">
                            <div className="relative top-[-2px] mr-[8px]">
                                <img src={icon} alt="icon" />
                            </div>

                            <PrimaryText>{mappedValue}</PrimaryText>
                        </div>
                    )
                }
            }

            if (formatting.resetBenefitDifference) {
                columnValue = this.getResetBenefitsDifferences(
                    coverageCheckHistory,
                    jsxSupportedFormatting,
                )
            }

            if (formatting.agenticCheckStatus) {
                if (
                    POLICIES_CONFIG.pendingAgenticStatuses.includes(
                        columnValue as AgenticCheckStatus,
                    )
                ) {
                    columnValue = jsxSupportedFormatting ? (
                        <AgenticCheckStatusComponent
                            backgroundFree={true}
                            agenticCheck={
                                coverageCheckHistory?.agenticCheck || null
                            }
                        />
                    ) : (
                        POLICIES_CONFIG.agenticCheckStatusMapping[
                            columnValue as AgenticCheckStatus
                        ]
                    )
                } else {
                    // For FINISHED and ERROR states, use the default icon and text
                    const icon =
                        POLICIES_CONFIG.agenticCheckStatusIconsMapping[
                            columnValue as AgenticCheckStatus
                        ]

                    const text =
                        POLICIES_CONFIG.agenticCheckStatusMapping[
                            columnValue as AgenticCheckStatus
                        ]

                    columnValue = jsxSupportedFormatting ? (
                        <div className="flex items-center">
                            <div className="mr-[8px]">
                                {icon ? (
                                    <img src={icon} alt="flag icon" />
                                ) : null}
                            </div>

                            <PrimaryText>
                                <div>{text}</div>
                            </PrimaryText>
                        </div>
                    ) : (
                        text
                    )
                }
            }

            if (formatting.taskAssignmentAssignee) {
                columnValue =
                    columnValue || POLICIES_COLUMNS_CONFIG.unassignedColumnValue

                columnValue = jsxSupportedFormatting ? (
                    <div className="flex items-center">
                        <PrimaryText
                            color={
                                columnValue ===
                                POLICIES_COLUMNS_CONFIG.unassignedColumnValue
                                    ? NIRVANA_COLORS.brand.error
                                    : undefined
                            }
                        >
                            {columnValue}
                        </PrimaryText>
                    </div>
                ) : (
                    columnValue
                )
            }

            if (formatting.appointmentDate) {
                const today = moment()

                const appointmentDate = moment(columnValue)

                const diff = appointmentDate.diff(today, "days")

                const color =
                    diff === 0 ? NIRVANA_COLORS.brand.error : undefined

                columnValue =
                    jsxSupportedFormatting && color ? (
                        <span style={{ color }}>{columnValue}</span>
                    ) : (
                        columnValue
                    )
            }

            if (formatting.latestComment) {
                columnValue = jsxSupportedFormatting ? (
                    <div className="flex items-center">
                        <div className="w-full" title={columnValue}>
                            <PrimaryText>
                                {typeof columnValue === "string" &&
                                columnValue.length > 50
                                    ? `${columnValue.substring(0, 50)}...`
                                    : columnValue}
                            </PrimaryText>
                        </div>
                    </div>
                ) : (
                    columnValue
                )
            }

            if (formatting.priority) {
                // Low - regular dark purple
                // Medium - orange
                // High - red
                const color =
                    columnValue === PolicyAssignmentPriority.LOW
                        ? NIRVANA_COLORS.brand.primary
                        : columnValue === PolicyAssignmentPriority.MEDIUM
                          ? NIRVANA_COLORS.brand.orange
                          : NIRVANA_COLORS.brand.error

                columnValue = jsxSupportedFormatting ? ( // Show as text with different colors
                    <span style={{ color }}>{columnValue}</span>
                ) : (
                    columnValue
                )
            }
        } catch (e) {}

        return columnValue
    }

    /**
     * Map list of flags to denial risk
     */
    static getPolicyDenialRisk(
        flags?: OneVerifyFlagType[] | null,
    ): PolicyDenialRisk {
        if (!flags?.length) {
            return PolicyDenialRisk.NONE
        }

        const mappings = flags.map(
            item => POLICIES_CONFIG.flagTypeMappings[item],
        )

        const maxSeverity = Math.max(...mappings.map(item => item.severity))

        if (maxSeverity >= POLICIES_CONFIG.highAttentionFlagSeverityCheck) {
            return PolicyDenialRisk.HIGH
        } else if (
            maxSeverity >= POLICIES_CONFIG.mediumAttentionFlagSeverityCheck
        ) {
            return PolicyDenialRisk.MEDIUM
        }

        return PolicyDenialRisk.LOW
    }

    /**
     * Get default columns for different views
     */
    static getDefaultColumns(
        viewType: PoliciesListType,
        availableModalities = [PolicyModality.MENTAL_HEALTH],
    ): IPolicyColumnConfiguration[] {
        switch (viewType) {
            case PoliciesListType.HISTORY_SEARCH:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.historicalPoliciesColumns,
                    PoliciesListType.HISTORY_SEARCH,
                    availableModalities,
                )

            case PoliciesListType.REPORTS:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.reportsPoliciesColumns,
                    PoliciesListType.REPORTS,
                    availableModalities,
                )

            case PoliciesListType.MEDICAID:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.medicaidReportsPoliciesColumns,
                    PoliciesListType.MEDICAID,
                    availableModalities,
                    // Pass true if need to add columns that are specific to modalities to medicaid, for now not showing
                    false,
                )

            case PoliciesListType.OVERRIDES:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.overridesPoliciesColumns,
                    PoliciesListType.OVERRIDES,
                    availableModalities,
                )

            case PoliciesListType.PLAN_YEAR_RESETS:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.planYearResetsPoliciesColumns,
                    PoliciesListType.PLAN_YEAR_RESETS,
                    availableModalities,
                )

            case PoliciesListType.AGENTIC:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.agenticReportsPoliciesColumns,
                    PoliciesListType.AGENTIC,
                    availableModalities,
                )

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.assignedTasksPoliciesColumns,
                    PoliciesListType.ASSIGNED_TASKS,
                    availableModalities,
                )

            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.assignedTasksPoliciesColumnsExtendedCustomFields,
                    PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS,
                    availableModalities,
                )

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.taskAssignmentPoliciesColumns,
                    PoliciesListType.TASK_ASSIGNMENTS,
                    availableModalities,
                )

            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
                return ExportReportHelper.processAdditionalColumns(
                    POLICIES_COLUMNS_CONFIG.taskAssignmentPoliciesColumnsExtendedCustomFields,
                    PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS,
                    availableModalities,
                )
        }
    }

    /**
     * Process additional global columns
     * Hide some of them, change order, or change default visibility
     */
    private static processAdditionalColumns(
        baseColumns: IPolicyColumnConfiguration[],
        listType: PoliciesListType,
        availableModalities = [PolicyModality.MENTAL_HEALTH],
        addModalitySpecificColumns = true,
    ): IPolicyColumnConfiguration[] {
        // Make a copy
        baseColumns = [...baseColumns]

        const additionalColumns = addModalitySpecificColumns
            ? [
                  ...ExportReportHelper.getModalitiesSpecificColumnsConfigurations(
                      availableModalities,
                  ),
              ].filter(item => !item.listTypeIgnored?.[listType])
            : []

        // Add columns with needed order to needed place
        for (const column of additionalColumns) {
            const orderIndex = column.listTypeOrder?.[listType]

            if (typeof orderIndex === "undefined") {
                continue
            }

            baseColumns.splice(orderIndex, 0, column)
        }

        const result = [
            ...baseColumns,

            // Add columns without needed order to the end
            ...additionalColumns.filter(
                item => typeof item.listTypeOrder?.[listType] === "undefined",
            ),
        ]

        // Override default visibility and being default by config from column itself
        return result.map(item => {
            const enabledOverride = item.listTypeEnabled?.[listType]

            const isEnabled =
                typeof enabledOverride !== "undefined"
                    ? enabledOverride
                    : item.isEnabled

            const isDefaultColumn =
                typeof enabledOverride !== "undefined"
                    ? enabledOverride
                    : item.isDefaultColumn

            // Apply listType-specific enumMapping for task assignment status columns
            let formatting = item.formatting

            if (
                item.columnKey === PolicyColumnKey.TASK_ASSIGNMENT_STATUS ||
                item.columnKey === PolicyColumnKey.LAST_DONE_STATUS
            ) {
                formatting = {
                    ...formatting,
                    enumMapping:
                        POLICIES_CONFIG.policyAssignmentStatusMapping(listType),
                }
            }

            return {
                ...item,

                isEnabled,
                isDefaultColumn,
                formatting,
            }
        })
    }

    /**
     * Get columns configurations specific to different modalities
     */
    static getModalitiesSpecificColumnsConfigurations(
        availableModalities = [PolicyModality.MENTAL_HEALTH],
    ): IPolicyColumnConfiguration[] {
        let result: IPolicyColumnConfiguration[] = []

        for (const modality of availableModalities) {
            result = [
                ...result,

                ...(POLICIES_COLUMNS_CONFIG.modalitySpecificColumns[modality] ||
                    []),
            ]
        }

        return result
    }

    /**
     * Resolve empty column value based on result type
     * The order of precedence is: config value, unknown coercion, provided emptyColumnValue
     * Note: This is not guaranteed to be the final value for an empty column, because anything resulting from
     * columnConfig.formatOnEmptyColumn, such as columnConfig.formatting.convertOnEmptyToUnknown, would override whatever is determined here
     */
    static resolveEmptyColumnValue(
        columnConfig: IPolicyColumnConfiguration,
        coverageCheckHistory: IOneVerifyCoverageCheckHistory,
        emptyColumnValue: string | null,
    ): string | null {
        const propertyPath =
            columnConfig.source === "coverageResult"
                ? columnConfig.sourcePropertyPath
                : columnConfig.alternativeSource === "coverageResult"
                  ? columnConfig.alternativeSourcePropertyPath
                  : undefined

        const unknownCoercion =
            propertyPath &&
            coverageCheckHistory.coverageResult.unknownFields.includes(
                propertyPath,
            )
                ? COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown
                : undefined

        return (
            columnConfig.emptyColumnValue ?? unknownCoercion ?? emptyColumnValue
        )
    }

    /**
     * Get reset benefits differences column value
     * In case of jsxSupportedFormatting, it will return JSX element with first 3 differences and rest count
     * This is so that we can have a concise UI for the table view but still have the full differences in the csv
     * We could potentially turn the '+n more' into a tooltip for the full list
     */
    private static getResetBenefitsDifferences(
        coverageCheck: IOneVerifyCoverageCheckHistory | null = null,
        jsxSupportedFormatting = true,
    ): string | JSX.Element | null {
        const result: string[] = []

        if (!coverageCheck?.coverageResult.resetBenefitsStatus) {
            return null
        }

        if (
            coverageCheck.coverageResult.resetBenefitsStatus !==
            ResetBenefitStatus.DETECTED_ACTION_REQUIRED
        ) {
            return COVERAGE_CONFIG.defaultFallbackCoverageDataValueNone
        }

        for (const columnConfig of POLICIES_COLUMNS_CONFIG.resetBenefitsColumns) {
            // resetBenefitsColumns should have this field
            if (!columnConfig.resetBenefitsCompareWith) {
                continue
            }

            const value = ExportReportHelper.getMappedColumnValue(
                coverageCheck,
                columnConfig,
                undefined,
                false,
            ) as string | null

            const valueDifference = ExportReportHelper.getMappedColumnValue(
                coverageCheck,
                POLICIES_COLUMNS_CONFIG.columnsConfig[
                    columnConfig.resetBenefitsCompareWith
                ],
                undefined,
                false,
            ) as string | null

            // If the fields are the same, it's not a difference
            // If both are empty, it's not a difference
            if (value === valueDifference) {
                continue
            }

            result.push(
                POLICIES_COLUMNS_CONFIG.columnsConfig[
                    columnConfig.resetBenefitsCompareWith
                ].label,
            )
        }

        if (!result.length) {
            return COVERAGE_CONFIG.defaultFallbackCoverageDataValueNone
        }

        if (jsxSupportedFormatting) {
            const firstThree = result.slice(0, 3)
            const rest = result.slice(3)

            return (
                <PrimaryText>
                    {firstThree.join(", ") +
                        (rest.length ? ` +${rest.length} more` : "")}
                </PrimaryText>
            )
        }

        return result.join(", ")
    }
}
